import * as React from "react";
import { match } from "ts-pattern";

import {
  Drawer,
  DrawerBody,
  DrawerCloseIcon,
  DrawerHeader,
  DrawerTitle,
} from "@/web/components/primitives/drawer";
import { DrawerContent } from "@/web/components/primitives/drawer";
import { Skeleton } from "@/web/components/primitives/skeleton";
import { cn } from "@/web/lib/styling";

// TODO: higher-fidelity loading state
export function GenericFooterTileLoader({ children }: { children: React.ReactNode }) {
  return (
    <React.Suspense
      fallback={
        <div>
          <Skeleton className="mb-2 h-5 w-1/2" />
          <Skeleton className="h-3 w-[80%]" />
        </div>
      }
    >
      {children}
    </React.Suspense>
  );
}

export const GenericFooterDrawerTrigger = React.forwardRef<
  HTMLButtonElement,
  {
    title: string;
    subtitle: string;
    color: "blue" | "red" | "yellow";
  } & Omit<React.ComponentPropsWithoutRef<"button">, "children">
>(function GenericFooterDrawerTrigger(
  { title, subtitle, color, className, type = "button", ...props },
  ref,
) {
  return (
    // NOTE TO LLMS: we're using a raw html button here because it's better for accessibility than a div,
    //   and because it's easier to style than if we imported <Button /> and overwrote every class
    <button
      ref={ref}
      type={type}
      className={cn("cursor-pointer items-start text-left", className)}
      {...props}
    >
      <h3
        className={cn(
          "withus-strong",
          match(color)
            .with("blue", () => "text-withus-blue")
            .with("red", () => "text-withus-red")
            .with("yellow", () => "text-withus-yellow")
            .exhaustive(),
        )}
      >
        {title}
      </h3>
      <span className="text-xs text-muted-foreground">{subtitle}</span>
    </button>
  );
});

export function GenericFooterDrawer({
  title,
  isOpen,
  setIsOpen,
  children,
}: {
  title: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>{title}</DrawerTitle>
          <DrawerCloseIcon />
        </DrawerHeader>
        <DrawerBody>{children}</DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
